import { useState, useCallback, useContext, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext } from "../context/auth.context";
import { useNotification } from "../components/Notification/NotificationProvider";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { CircularProgress } from "../components/CircularProgress/CircularProgress";
import { validateEmail } from "../utils/email";
import { apiErrorHasCode, getApiErrorMessage } from "../utils/getApiErrorMessage";
import { UnconfirmedEmailModal } from "../components/UnconfirmedEmailModal/UnconfirmedEmailModal";

export const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [unconfirmedEmailOpen, setUnconfirmedEmailOpen] = useState(false);

  const { login, resetPassword, resendConfirmEmail } = useContext(AuthContext);
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    if (!username || !password) return;
    setError(null);

    login(username, password).then(() => {
      setUsername("");
      setPassword("");
      setError(null);
      navigate('/');
    }).catch((e: unknown) => {
      if (apiErrorHasCode(e, "ERR-015")) {
        setError(null);
        setUnconfirmedEmailOpen(true);
        return;
      }
      if (apiErrorHasCode(e, "ERR-011") || apiErrorHasCode(e, "ERR-012")) {
        setError(getApiErrorMessage(e, "Ошибка авторизации"));
        return;
      }
      const response = (e as { response?: { status?: number; data?: { message?: string } } })
        .response;
      if (response?.status === 401) {
        setError("Неверный логин или пароль");
      } else {
        setError(getApiErrorMessage(e, "Ошибка авторизации"));
      }
    });
  }, [login, username, password, navigate]);

  const handleResetPassword = useCallback(() => {
    const email = username.trim();
    if (!validateEmail(email)) {
      showNotification({ message: "Введите корректный email для сброса пароля", variant: "error" });
      return;
    }
    setResetLoading(true);
    resetPassword(email)
      .then(() => {
        showNotification({ message: "Инструкция по сбросу пароля отправлена", variant: "success" });
      })
      .catch((e: unknown) => {
      })
      .finally(() => {
        setResetLoading(false);
      });
  }, [resetPassword, showNotification, username]);

  return (
    <>
      {unconfirmedEmailOpen && (
        <UnconfirmedEmailModal
          email={username}
          onClose={() => setUnconfirmedEmailOpen(false)}
          onResend={resendConfirmEmail}
        />
      )}
    <div className="auth-container">
      <div className="auth-floating-wrap">
        <Link to="/" className="auth-home-link auth-home-link--floating" aria-label="На главную">
          <ArrowLeft size={14} />
          <span className="auth-home-link-label">На главную</span>
        </Link>
        <form className="auth-form" onSubmit={handleSubmit}>
          <h1 className="auth-title">I N A R I</h1>

        <div className="form-group">
          <input
            type="text"
            className={`input ${error ? 'input-error' : ''}`}
            placeholder="Введите ваш E-mail"
            value={username}
            autoFocus
            autoComplete="off"
            onChange={e => {
              setError(null);
              setUsername(e.target.value);
            }}
          />
        </div>

        <div className="form-group">
          <div className="input-password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              className={`input ${error ? 'input-error' : ''}`}
              placeholder="Введите ваш пароль"
              value={password}
              autoComplete="current-password"
              onChange={e => {
                setError(null);
                setPassword(e.target.value.replace(/[а-яёА-ЯЁ]/g, ''));
              }}
            />
            {password && (
              <button
                type="button"
                className="input-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            )}
          </div>
        </div>

        <div className="button-wrapper">
          <button
            className="btn btn-primary"
            type="submit"
            disabled={!username || !password}
          >
            Войти
          </button>
          {error && <div className="form-error">{error}</div>}
        </div>

        <div className="footer-links">
          <button
            type="button"
            className="auth-link auth-link-button"
            onClick={handleResetPassword}
            disabled={resetLoading}
          >
            {resetLoading ? <CircularProgress size="sm" /> : "Сбросить пароль"}
          </button>
          <Link to="/register" className="auth-link">
            Зарегистрироваться
          </Link>
        </div>
        </form>
      </div>
    </div>
    </>
  );
};
